import { DEFAULT_LIMIT, MAX_PAGINATION_LIMIT, DEFAULT_OFFSET } from '@zaoblako/shared/common/dtos/pagination-query.dto';
import { isNil } from 'lodash';
import { forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

export function isSameId(left: unknown, right: unknown): boolean {
  if (isNil(left) || isNil(right)) {
    return false;
  }

  return String(left) === String(right);
}

export function chunkIds<T>(ids: T[], chunkSize = MAX_PAGINATION_LIMIT): T[][] {
  if (!ids.length) {
    return [];
  }

  const chunks: T[][] = [];

  for (let offset = DEFAULT_OFFSET; offset < ids.length; offset += chunkSize) {
    chunks.push(ids.slice(offset, offset + chunkSize));
  }

  return chunks;
}

export function fetchAllByIncludeIds<T, TId>(
  ids: TId[],
  fetchChunk: (chunkIds: TId[]) => Observable<{ items: T[]; }>
): Observable<T[]> {
  if (!ids.length) {
    return of([]);
  }

  return forkJoin(chunkIds(ids).map((chunk) => fetchChunk(chunk))).pipe(map((pages) => pages.flatMap((page) => page.items)));
}

export function isStrictHydrationRequest(query: {
  includeIds?: unknown[];
  excludeIds?: unknown[];
  search?: string;
  limit?: number;
}): boolean {
  const includeIds = query.includeIds;
  if (!includeIds?.length || query.excludeIds?.length || query.search) {
    return false;
  }

  const limit = query.limit ?? DEFAULT_LIMIT;
  return includeIds.length === limit;
}

export function mergeUniqueIds<TId>(...sources: (TId[] | undefined)[]): TId[] {
  const merged = new Set<TId>();
  for (const source of sources) {
    source?.forEach((id) => merged.add(id));
  }
  return [...merged];
}

export function buildAppendPageQuery<TId>({
  limit,
  inputExcludeIds,
  loadedIds
}: {
  limit: number;
  inputExcludeIds?: TId[];
  loadedIds: TId[];
}): { limit: number; offset: number; excludeIds: TId[]; } {
  return {
    limit,
    offset: DEFAULT_OFFSET,
    excludeIds: mergeUniqueIds(inputExcludeIds, loadedIds)
  };
}

export function buildDeferredPageQuery<TId>({
  limit,
  selectedIds,
  inputExcludeIds
}: {
  limit: number;
  selectedIds: TId[];
  inputExcludeIds?: TId[];
}): { limit: number; offset: number; excludeIds: TId[]; } {
  return {
    limit,
    offset: DEFAULT_OFFSET,
    excludeIds: mergeUniqueIds(inputExcludeIds, selectedIds)
  };
}
